import React, { useEffect, useRef } from 'react';
import queryString from 'query-string';
import { showLoading, hideLoading } from 'react-redux-loading-bar';

import { IProps } from './types';
import {
  ActionForAuthentication,
  GoogleOneTapStashedPayload,
} from '../../types';
import { GOOGLE_ONE_TAP_SESSION_KEY } from '../oauth-buttons/constants';
import { AuthCredentialType } from '../../enums/auth-credential-type';

import toaster, { Theme } from '../../../../shared/toaster';

import { OAuthMethod } from '../oauth-buttons';

import store from '../../../../store';
import { AuthHelper } from '../../../../shared/utils/auth-helper';
// import { AnalyticsEvents } from '../../../../shared/enums/analytics';
import {
  // buildPersonDisplayName,
  // capturePosthogEvent,
  // getPosthogPageSnapshotAtCapture,
  initializeThirdPartyIntegrations,
} from '../../../../shared/utils/initialize-third-party-integrations';
import {
  oauthMethodToGtmSignupMethod,
  pushGtmSignupComplete,
} from '../../../../shared/utils/gtm-attribution';
import { trackToltSignup } from '../../../../shared/utils/tolt';
import { RequestStatus } from '../../../../shared/enums/request-status';
import {
  executeOnRequestStatus,
  getCurrentTimeZone,
  redirectWithoutRefresh,
  setToken,
} from '../../../../shared/utils';

const OAuthAuthenticateCallback: React.FC<IProps> = (props) => {
  const {
    match,
    location,
    history,

    oauthAuthenticateRequestStatus,
    oauthAuthenticateRequestMessage,
    oauthAuthenticateRequestError,
    sendOAuthAuthenticateRequest,

    token,
    trackingId,
    firstName,
    lastName,
    email,
    action,
    agencyConfig,
  } = props;

  const redirectToHome = () => {
    history.push('/warmup');
  };

  const redirectToLogin = () => {
    history.push('/login');
  };

  /** Prevents duplicate PostHog / side-effects when status stays Succeeded but effect re-runs (token/email deps). */
  const oauthSuccessHandledRef = useRef(false);

  // Handle initial authentication
  useEffect(() => {
    const { method } = match.params;
    const query = location.search;
    location.search = '';

    const {
      code,
      state,
      error: queryError,
      source,
      credential: credentialFromUrl,
      action: actionFromUrl,
      utmSource: utmSourceFromUrl,
    } = queryString.parse(query);

    let oneTapPayload: GoogleOneTapStashedPayload | null = null;
    if (source === 'one-tap' && !code) {
      if (credentialFromUrl) {
        oneTapPayload = {
          credential: credentialFromUrl.toString(),
          action: actionFromUrl?.toString() as
            | ActionForAuthentication
            | undefined,
          utmSource: utmSourceFromUrl?.toString(),
        };
      } else {
        try {
          const raw = window.sessionStorage.getItem(GOOGLE_ONE_TAP_SESSION_KEY);
          if (raw) {
            oneTapPayload = JSON.parse(raw);
          }
        } catch {
          oneTapPayload = null;
        }
      }
    }
    try {
      window.sessionStorage.removeItem(GOOGLE_ONE_TAP_SESSION_KEY);
    } catch {
      /* no-op */
    }

    if (queryError) {
      toaster.error('Authentication failed', {
        theme: Theme.New,
        showCloseIcon: true,
      });
      redirectToLogin();
    } else if (code) {
      // OAuth-redirect flow.
      const oauthMethod = method as OAuthMethod;
      sendOAuthAuthenticateRequest(
        oauthMethod,
        code.toString(),
        state?.toString(),
      );
      store.dispatch(showLoading());
    } else if (oneTapPayload?.credential) {
      // Google One Tap flow.
      const oauthMethod = method as OAuthMethod;
      sendOAuthAuthenticateRequest(
        oauthMethod,
        oneTapPayload.credential,
        undefined,
        {
          credentialType: AuthCredentialType.GoogleIdToken,
          action: oneTapPayload.action,
          utmSource: oneTapPayload.utmSource,
          rewardfulReferralId: oneTapPayload.rewardfulReferralId,
          timeZone: getCurrentTimeZone(),
        },
      );
      store.dispatch(showLoading());
    } else {
      // No code, no One Tap credential, no error in the URL.
      redirectToLogin();
    }
  }, []);

  // Handle authentication success
  useEffect(() => {
    executeOnRequestStatus({
      status: oauthAuthenticateRequestStatus,
      onSuccess: () => {
        if (oauthSuccessHandledRef.current || !token || !email) {
          return;
        }
        oauthSuccessHandledRef.current = true;

        store.dispatch(hideLoading());
        if (token && email) {
          switch (action) {
            case ActionForAuthentication.Signup: {
              setToken(token);

              initializeThirdPartyIntegrations({
                trackingId,
                showChatSupport: agencyConfig?.showChatSupport,
                firstName,
                lastName,
                email,
              });

              const signupMethod = oauthMethodToGtmSignupMethod(
                match.params.method,
              );
              if (signupMethod) {
                pushGtmSignupComplete({ signupMethod });
              }
              trackToltSignup(email);

              // capturePosthogEvent(AnalyticsEvents?.SignupCompleted, {
              //   'User Email': email,
              //   'User Name': buildPersonDisplayName(firstName, lastName),
              //   source: 'web_oauth_signup',
              //   oauth_provider: match.params.method,
              //   ...getPosthogPageSnapshotAtCapture(),
              // });

              redirectWithoutRefresh({
                pathname: '/warmup',
                search: 'signup=completed',
              });
              break;
            }

            case ActionForAuthentication.Login:
            default:
              AuthHelper.login({ token });

              initializeThirdPartyIntegrations({
                trackingId,
                showChatSupport: agencyConfig?.showChatSupport,
                firstName,
                lastName,
                email,
              });

              // capturePosthogEvent(AnalyticsEvents?.SignedIn, {
              //   'User Email': email,
              //   source: 'web_oauth_login',
              //   oauth_provider: match.params.method,
              // });

              redirectToHome();
              toaster.success(oauthAuthenticateRequestMessage, {
                theme: Theme.New,
              });
          }
        }
      },
      onFailed: () => {
        oauthSuccessHandledRef.current = false;
        store.dispatch(hideLoading());
        // Errors already toasted by the global api interceptor (isHandled) are
        // stored as falsy, so skip the fallback toast to avoid a duplicate.
        if (oauthAuthenticateRequestError) {
          toaster.error(oauthAuthenticateRequestError.message, {
            theme: Theme.New,
            showCloseIcon: true,
            delay: 10000,
          });
        }
        history.push({
          pathname: '/login',
          state: { showV2MigrationLink: true },
        });
      },
    });

    if (oauthAuthenticateRequestStatus !== RequestStatus.Succeeded) {
      oauthSuccessHandledRef.current = false;
    }
  }, [oauthAuthenticateRequestStatus, token, email]);

  return null;
};

export default OAuthAuthenticateCallback;
